import { FacetTypes } from '../../../facets/Facets';

/** Map a model-local Z elevation to scene height using placementWithOffset. */
export function transformModelZ(placementWithOffset, z) {
    if (z == null || !placementWithOffset) {
        return z;
    }

    return placementWithOffset.elements[10] * z + placementWithOffset.elements[14];
}

/**
 * Returns scene-space elevations keyed by level name for the active level source.
 * Uses LBS Location levels when available; otherwise falls back to source-model levels.
 */
export function getLevelElevationByName(
    facility, hudLevels = facility.facetsManager?.getHudLevelNodes()
) {
    if (hudLevels?.facetType === FacetTypes.locations) {
        return getLocationLevelElevByName(facility, hudLevels.nodes);
    }

    return getSourceModelLevelElevByName(facility);
}

function getLocationLevelElevByName(facility, levels) {
    const result = {};

    const defaultModel = facility.getDefaultModel?.();
    const placement = defaultModel?.getData()?.placementWithOffset;

    for (const level of levels) {
        if (level.isUnassignedId || level.elev == null) continue;

        result[level.id] = transformModelZ(placement, level.elev);
    }

    return result;
}

function getSourceModelLevelElevByName(facility) {
    const elevationsByName = {};

    for (const model of facility.getModels()) {
        if (!model.getData() || model.isDefault()) continue;

        // Uses the encoded elevation for this model and transform it to scene height.
        const { placementWithOffset } = model.getData();
        if (!placementWithOffset) continue;

        const levels = model.getLevels();

        for (const dbId in levels) {
            const { name, elev } = levels[dbId];
            if (elev == null) continue;

            const elevations = elevationsByName[name] ?? (elevationsByName[name] = []);
            elevations.push(transformModelZ(placementWithOffset, elev));
        }
    }

    // Reduce to averages.
    const avg = (arr) => arr?.length ? arr.reduce((sum, v) => sum + v, 0) / arr.length : 0;
    const avgByName = {};
    for (const name in elevationsByName) {
        avgByName[name] = avg(elevationsByName[name]);
    }
    return avgByName;
}

/**
 * Magnitude along the x and y axes.
 * @typedef {{x:number, y:number}} Vec2
 */

/**
 * Returned the signed non-normalized distanced between the point and line defined by 2 points.
 * @param {Vec2} point Point
 * @param {Vec2} lineStart Start
 * @param {Vec2} lineEnd End
 */
function orientedDistancePointToLine(point, lineStart, lineEnd) {
    // Per eq. (14) https://mathworld.wolfram.com/Point-LineDistance2-Dimensional.html
    // Not returning a normalized distance since we care only about the farthest away, and using oriented distance
    // to allow separating points by which side of the line they are on.
    return (lineEnd.x - lineStart.x) * (lineStart.y - point.y) - (lineStart.x - point.x) * (lineEnd.y - lineStart.y);
}

/**
 *  Fills scoped with all points on the normal side of the line, and return farthest point if present.
 * @param {Vec2} lineStart Start
 * @param {Vec2} lineEnd End
 * @param {Vec2[]} pts Point cloud to segment
 * @param {Vec2[]} scoped Output collection where oriented pts are placed.
 */
function getOrientedPts(lineStart, lineEnd, pts, scoped) {
    let farthest, max;

    max = -Infinity;
    for(let i = 0; i < pts.length; i++) {
        let pt = pts[i];
        let dst = orientedDistancePointToLine(pt, lineStart, lineEnd);

        if (dst <= 0) continue; // Wrong side of the line or on it.
        else scoped.push(pt);

        if (dst > max) {
            farthest = pt;
            max = dst;
        }
    }

    return farthest;
}

/**
 * Planar quick hull algorithm.
 * Based on https://en.wikipedia.org/wiki/Quickhull with
 * stack optimization from https://github.com/claytongulick/quickhull
 * @param {Vec2[]} pts Points.
 * @return {Vec2[]} Ordered points of a convex hull.
 */
export function getConvexHull2D(pts) {
    if (pts.length <= 3) return pts.length ? [...pts, pts[0]] : null;

    // Get the min and max to place the initial dividing line.
    let min, max;
    min = max = pts[0];
    for(let i = 1; i < pts.length; i++) {
        const { x, y } = pts[i];
        if (x <= min.x) {
            if (x < min.x) min = pts[i];
            else if (y < min.y) min = pts[i];
        }
        if (x >= max.x) {
            if (x > max.x) max = pts[i];
            else if (y > max.y) max = pts[i];
        }
    }

    const hull = [];
    function FindHull(lineStart, lineEnd, pts) {
        const orientedPts = [];
        const farthest = getOrientedPts(lineStart, lineEnd, pts, orientedPts);
        if (farthest == null) return hull.push(lineStart);

        FindHull(lineStart, farthest, orientedPts);
        FindHull(farthest, lineEnd, orientedPts);
    }

    // Segments are added in both directions since each only consider the points in its normal direction.
    FindHull(min, max, pts);
    FindHull(max, min, pts);

    // Make it a closed path
    hull.push(hull[0]);

    return hull;
}
